import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import { format, eachDayOfInterval, isWeekend } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2, XCircle, Save, Users, Clock, Eye, Download, CheckCircle, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { db, auth } from "../firebase/config";
import { collection, query, where, onSnapshot, doc, setDoc, writeBatch,  Timestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import useSuspensionCheck from "./useSuspensionCheck";
import SuspensionWarning from "./SuspensionWarning";

export interface AttendanceRecord {
  id: string;
  studentId: string;
  date: string;
  status: 'present' | 'absent';
  reason?: string | null;
  markedBy: string;
  markedAt: string;
  classId: string;
}

interface ClassData {
  id: string;
  className: string;
  students: { id: string; firstName: string; middleName?: string; lastName: string; registrationNumber: string; photoUrl?: string }[];
}

const getFullName = (student: { firstName: string; middleName?: string; lastName: string }) => {
  return [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ");
};

interface AttendanceTakingProps {
  classData: ClassData;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  schoolID: string;
}

interface StudentAttendanceState {
  studentId: string;
  status: 'present' | 'absent' | 'unmarked';
  reason?: string | null;
}

const AttendanceTaking = ({ 
  classData, 
  selectedDate, 
  onDateChange, 
  schoolID 
}: AttendanceTakingProps) => {
  const [attendanceStates, setAttendanceStates] = useState<StudentAttendanceState[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const { toast } = useToast();

  const commonReasons = [
    "Sick leave",
    "Family matter", 
    "Medical appointment",
    "Personal emergency",
    "Transportation issue",
    "Other"
  ];

  // Load default absence reason from Firestore
  useEffect(() => {
    if (!schoolID || !classData.id || !auth.currentUser) return;

    const preferencesRef = doc(db, `schools/${schoolID}/preferences/${classData.id}`);
    const unsubscribe = onSnapshot(preferencesRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const reason = data.defaultAbsenceReason || "";
        if (commonReasons.includes(reason)) {
          setSelectedReason(reason);
          setCustomReason("");
        } else {
          setSelectedReason("Other");
          setCustomReason(reason);
        }
        if (process.env.NODE_ENV !== "production") {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Loaded default absence reason for class ${classData.id}:`, reason);
        }
      } else {
        setSelectedReason("");
        setCustomReason("");
      }
    }, (error) => {
      console.error('Error fetching default absence reason:', error);
      toast({
        title: "Error",
        description: "Failed to load default absence reason. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    });

    return () => unsubscribe();
  }, [schoolID, classData.id, toast]);

  // Save default absence reason to Firestore with debouncing
  const saveDefaultReason = useMemo(() => debounce(async (reason: string) => {
    if (!schoolID || !classData.id || !auth.currentUser || !reason) return;

    const preferencesRef = doc(db, `schools/${schoolID}/preferences/${classData.id}`);
    try {
      await setDoc(preferencesRef, {
        defaultAbsenceReason: reason,
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser.email || 'Unknown'
      }, { merge: true });
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Saved default absence reason for class ${classData.id}:`, reason);
      }
    } catch (error) {
      console.error('Error saving default absence reason:', error);
      toast({
        title: "Error",
        description: "Failed to save default absence reason. You may lack permissions.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, 1000), [schoolID, classData.id, toast]);

  useEffect(() => {
    const reason = selectedReason === 'Other' ? customReason : selectedReason;
    if (reason) {
      saveDefaultReason(reason);
    }
  }, [selectedReason, customReason, saveDefaultReason]);

  useEffect(() => {
    if (!schoolID || !classData.id) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const recordsRef = collection(db, `schools/${schoolID}/attendance/${dateStr}/records`);
    const q = query(recordsRef, where('classId', '==', classData.id));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: dateStr,
        reason: doc.data().reason ?? null
      } as AttendanceRecord));
      setAttendanceRecords(records);
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Fetched attendance records for ${dateStr}:`, records);
      }
    }, (error) => {
      console.error('Error fetching attendance:', error);
      toast({
        title: "Error",
        description: "Failed to load attendance records. Please check Firebase console for index errors.",
        variant: "destructive",
        duration: 5000,
      });
    });

    return () => unsubscribe();
  }, [selectedDate, schoolID, classData.id, toast]);

  useEffect(() => {
    const states = classData.students.map(student => {
      const existingRecord = attendanceRecords.find(r => r.studentId === student.id);
      return {
        studentId: student.id,
        status: existingRecord?.status || 'unmarked' as const,
        reason: existingRecord?.reason ?? null
      };
    });
    setAttendanceStates(states);
  }, [attendanceRecords, classData.students]);

  const updateStudentAttendance = (studentId: string, status: 'present' | 'absent', reason?: string | null) => {
    setAttendanceStates(prev => 
      prev.map(state => 
        state.studentId === studentId 
          ? { ...state, status, reason: status === 'present' ? null : (reason || (selectedReason === 'Other' ? customReason : selectedReason) || null) }
          : state
      )
    );
  };

  const markAllPresent = () => {
    setAttendanceStates(prev => 
      prev.map(state => ({ ...state, status: 'present' as const, reason: null }))
    );
  };

  const markAllAbsent = () => {
    const reason = selectedReason === 'Other' ? customReason : selectedReason;
    setAttendanceStates(prev => 
      prev.map(state => ({ ...state, status: 'absent' as const, reason: reason || null }))
    );
  };

  const saveAllAttendance = async () => {
    if (!schoolID || !auth.currentUser) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const batch = writeBatch(db);
    let attempts = 0;
    const maxAttempts = 3;

    try {
      const idTokenResult = await auth.currentUser.getIdTokenResult();
      const isAuthorized = (idTokenResult.claims.defaultAffiliation as { role?: string })?.role === 'admin' ||
        (typeof idTokenResult.claims.defaultAffiliation === 'object' && 
        (idTokenResult.claims.defaultAffiliation as { role?: string }).role === 'school_staff') ||
        Array.isArray(idTokenResult.claims.affiliations) && idTokenResult.claims.affiliations.some((aff: any) => ['admin', 'school_staff'].includes(aff.role));

      if (!isAuthorized) {
        throw new Error('User not authorized to save attendance');
      }

      for (const state of attendanceStates) {
        if (state.status !== 'unmarked') {
          const recordRef = doc(db, `schools/${schoolID}/attendance/${dateStr}/records`, state.studentId);
          const newRecord: AttendanceRecord = {
            id: state.studentId,
            studentId: state.studentId,
            date: dateStr,
            status: state.status,
            reason: state.reason ?? null,
            markedBy: auth.currentUser.email || 'Unknown',
            markedAt: new Date().toISOString(),
            classId: classData.id
          };
          batch.set(recordRef, newRecord);
        }
      }

      while (attempts < maxAttempts) {
        try {
          await batch.commit();
          toast({
            title: "Success",
            description: "Attendance saved successfully!",
            variant: "default",
            duration: 3000,
          });
          if (process.env.NODE_ENV !== "production") {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Saved attendance records for ${dateStr}:`, attendanceStates.filter(s => s.status !== 'unmarked'));
          }
          return;
        } catch (error) {
          attempts++;
          if (attempts === maxAttempts) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } catch (error) {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error saving attendance:`, error);
      toast({
        title: "Error",
        description: "Failed to save attendance. Please check Firebase console for index errors.",
        variant: "destructive",
        duration: 5000,
        action: <Button onClick={saveAllAttendance}>Retry</Button>,
      });
    }
  };

  const getStatusCounts = useMemo(() => {
    const present = attendanceStates.filter(s => s.status === 'present').length;
    const absent = attendanceStates.filter(s => s.status === 'absent').length;
    const unmarked = attendanceStates.filter(s => s.status === 'unmarked').length;
    return { present, absent, unmarked };
  }, [attendanceStates]);

  return (
    <div className="space-y-6">
      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Take Attendance
              </CardTitle>
              <CardDescription>
                {classData.className} • {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("justify-start text-left font-normal w-[180px]")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && onDateChange(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
        <CardHeader>
          <CardTitle className="text-lg">Bulk Actions</CardTitle>
          <CardDescription>
            Quickly mark attendance for all students, then edit individual exceptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button onClick={markAllPresent} variant="outline" className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="hidden xs:inline">Mark All Present</span>
                <span className="xs:hidden">All Present</span>
              </Button>
              <Button 
                onClick={markAllAbsent} 
                variant="outline" 
                className="flex items-center gap-2 text-sm"
                disabled={!selectedReason && !customReason}
              >
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="hidden xs:inline">Mark All Absent</span>
                <span className="xs:hidden">All Absent</span>
              </Button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Default Absence Reason (for bulk marking)</Label>
                <Select value={selectedReason} onValueChange={setSelectedReason}>
                  <SelectTrigger className="w-full bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {commonReasons.map(reason => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedReason === 'Other' && (
                <div className="space-y-2">
                  <Label className="text-sm">Custom Reason</Label>
                  <Input
                    placeholder="Enter custom reason..."
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-4 m-3">
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{getStatusCounts.present}</div>
          <div className="text-sm text-green-700">Present</div>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <div className="text-2xl font-bold text-red-600">{getStatusCounts.absent}</div>
          <div className="text-sm text-red-700">Absent</div>
        </div>
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">{getStatusCounts.unmarked}</div>
          <div className="text-sm text-yellow-700">Unmarked</div>
        </div>
      </div>

      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Student Attendance ({classData.students.length} students)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {classData.students.map((student, index) => {
              const state = attendanceStates.find(s => s.studentId === student.id);
              return (
                <div key={student.id || `student_${index}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 border rounded-lg bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                          {student.photoUrl ? (
                              <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                              <span className="text-[10px] font-bold text-slate-500">
                                  {getFullName(student).split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                          )}
                      </div>
                      <div className="font-medium text-sm sm:text-base truncate">{getFullName(student)}</div>
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground ml-10">Reg No: {student.registrationNumber}</div>
                  </div>
                  <div className="flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Button
                        size="sm"
                        variant={state?.status === 'present' ? 'default' : 'outline'}
                        onClick={() => updateStudentAttendance(student.id, 'present')}
                        className="flex items-center gap-1 h-8 px-3 text-xs sm:text-sm"
                      >
                        <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        Present
                      </Button>
                      <Button
                        size="sm"
                        variant={state?.status === 'absent' ? 'destructive' : 'outline'}
                        onClick={() => updateStudentAttendance(student.id, 'absent', state?.reason)}
                        className="flex items-center gap-1 h-8 px-3 text-xs sm:text-sm"
                      >
                        <XCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                        Absent
                      </Button>
                    </div>
                    {state?.status === 'absent' && (
                      <div className="w-full sm:w-36 sm:ml-3">
                        <Select 
                          value={state.reason || ''} 
                          onValueChange={(value) => updateStudentAttendance(student.id, 'absent', value)}
                        >
                          <SelectTrigger className="w-full sm:w-36 h-8 text-xs sm:text-sm bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <SelectValue placeholder="Reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {commonReasons.map(reason => (
                              <SelectItem key={reason} value={reason} className="text-xs sm:text-sm">{reason}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end m-3">
        <Button
          onClick={saveAllAttendance}
          disabled={getStatusCounts.unmarked === classData.students.length}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
        >
          <Save className="h-4 w-4" />
          Save Attendance
        </Button>
      </div>
    </div>
  );
};

interface AttendanceRecordsProps {
  classData: ClassData;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  schoolID: string;
}

const AttendanceRecords = ({ 
  classData, 
  selectedDate, 
  onDateChange,
  schoolID 
}: AttendanceRecordsProps) => {
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);

  useEffect(() => {
    if (!schoolID || !classData.id) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const recordsRef = collection(db, `schools/${schoolID}/attendance/${dateStr}/records`);
    const q = query(recordsRef, where('classId', '==', classData.id));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: dateStr,
        reason: doc.data().reason ?? null
      } as AttendanceRecord));
      setAttendanceRecords(records);
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Fetched attendance records for ${dateStr}:`, records);
      }
    }, (error) => {
      console.error('Error fetching attendance:', error);
    });

    return () => unsubscribe();
  }, [selectedDate, schoolID, classData.id]);

  const getRecordsForDate = () => attendanceRecords;

  const getStudentAttendanceForDate = useMemo(() => {
    const records = getRecordsForDate();
    return classData.students.map((student) => {
      const record = records.find(r => r.studentId === student.id);
      return {
        ...student,
        status: record?.status || 'unmarked',
        reason: record?.reason ?? null,
        markedAt: record?.markedAt
      };
    });
  }, [attendanceRecords, classData.students]);

  const getDateStats = useMemo(() => {
    const records = getRecordsForDate();
    const present = records.filter(r => r.status === 'present').length;
    const absent = records.filter(r => r.status === 'absent').length;
    const total = classData.students.length;
    const unmarked = total - present - absent;
    const attendanceRate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { present, absent, unmarked, total, attendanceRate };
  }, [attendanceRecords, classData.students.length]);

  const exportAttendance = () => {
    const csvContent = [
      'Reg Number,Student Name,Status,Reason,Marked At',
      ...getStudentAttendanceForDate.map(student => 
        `${student.registrationNumber},${getFullName(student)},${student.status},${student.reason || ''},${student.markedAt || ''}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${format(selectedDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-2">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:inline">View Attendance Records</span>
                <span className="xs:hidden">Attendance</span>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                <span className="hidden sm:inline">{classData.className} • {format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                <span className="sm:hidden">{format(selectedDate, 'MMM d, yyyy')}</span>
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("justify-start text-left font-normal text-sm")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <span className="hidden xs:inline">{format(selectedDate, "MMM d, yyyy")}</span>
                    <span className="xs:hidden">{format(selectedDate, "M/d")}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && onDateChange(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <Button variant="outline" onClick={exportAttendance} className="flex items-center gap-2 text-sm">
                <Download className="h-4 w-4" />
                <span className="hidden xs:inline">Export</span>
                <span className="xs:hidden">CSV</span>
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-lg sm:text-2xl font-bold text-green-600">{getDateStats.present}</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Present</div>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-lg sm:text-2xl font-bold text-red-600">{getDateStats.absent}</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Absent</div>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-lg sm:text-2xl font-bold text-yellow-600">{getDateStats.unmarked}</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Unmarked</div>
          </CardContent>
        </Card>
        <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{getDateStats.attendanceRate}%</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Rate</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 m-3 flex-wrap">
        <Button 
          variant={viewMode === 'summary' ? 'default' : 'outline'}
          onClick={() => setViewMode('summary')}
          className={cn(
            "text-sm px-3 m-1",
            viewMode === 'summary'
              ? "bg-[#2F5FCD] text-white border-[#2F5FCD] hover:bg-[#3A72EC] hover:border-[#3A72EC]"
              : "bg-[#FFFFFF] dark:bg-[#020817] border-[#E8EDF4] dark:border-[#1E293B] hover:bg-[#F1F5F9] dark:hover:bg-[#1E293B]"
          )}
        >
          <span className="hidden xs:inline">Summary View</span>
          <span className="xs:hidden">Summary</span>
        </Button>
        <Button 
          variant={viewMode === 'detailed' ? 'default' : 'outline'}
          onClick={() => setViewMode('detailed')}
          className={cn(
            "text-sm px-3 m-1",
            viewMode === 'detailed'
              ? "bg-[#2F5FCD] text-white border-[#2F5FCD] hover:bg-[#3A72EC] hover:border-[#3A72EC]"
              : "bg-[#FFFFFF] dark:bg-[#020817] border-[#E8EDF4] dark:border-[#1E293B] hover:bg-[#F1F5F9] dark:hover:bg-[#1E293B]"
          )}
        >
          <span className="hidden xs:inline">Detailed View</span>
          <span className="xs:hidden">Detailed</span>
        </Button>
      </div>

      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Attendance Records</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {getDateStats.present + getDateStats.absent} of {getDateStats.total} students marked
          </CardDescription>
        </CardHeader>
        <CardContent>
          {viewMode === 'summary' ? (
            <div className="space-y-3">
              {getStudentAttendanceForDate.map((student, index) => (
                <div key={student.id || `student_${index}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 sm:justify-between p-3 border rounded-lg bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                          {student.photoUrl ? (
                              <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                              <span className="text-[10px] font-bold text-slate-500">
                                  {getFullName(student).split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                          )}
                      </div>
                      <div className="font-medium text-sm sm:text-base truncate">{getFullName(student)}</div>
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground ml-10">Reg No: {student.registrationNumber}</div>
                  </div>
                  <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2">
                    {student.status === 'present' && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Present
                      </Badge>
                    )}
                    {student.status === 'absent' && (
                      <>
                        <Badge variant="destructive" className="text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Absent
                        </Badge>
                        {student.reason && (
                          <span className="text-xs text-muted-foreground">({student.reason})</span>
                        )}
                      </>
                    )}
                    {student.status === 'unmarked' && (
                      <Badge variant="outline" className="text-xs">Not Marked</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="block sm:hidden">
                <div className="space-y-3">
                  {getStudentAttendanceForDate.map((student, index) => (
                    <div key={student.id || `student_${index}`} className="p-3 border rounded-lg space-y-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{getFullName(student)}</div>
                          <div className="text-xs text-muted-foreground">Reg No: {student.registrationNumber}</div>
                        </div>
                        <div>
                          {student.status === 'present' && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Present
                            </Badge>
                          )}
                          {student.status === 'absent' && (
                            <Badge variant="destructive" className="text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              Absent
                            </Badge>
                          )}
                          {student.status === 'unmarked' && (
                            <Badge variant="outline" className="text-xs">Not Marked</Badge>
                          )}
                        </div>
                      </div>
                      {(student.reason || student.markedAt) && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          {student.reason && <div>Reason: {student.reason}</div>}
                          {student.markedAt && <div>Marked: {format(new Date(student.markedAt), 'h:mm a')}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-[#F8FAFC] dark:hover:bg-[#101929] border-b dark:border-[#1E293B] border-[#E2E8F0]">
                      <TableHead>Reg No.</TableHead>
                      <TableHead>Student Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Marked At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getStudentAttendanceForDate.map((student, index) => (
                      <TableRow key={student.id || `student_${index}`} className="hover:bg-[#F8FAFC] dark:hover:bg-[#101929] border-b dark:border-[#1E293B] border-[#E2E8F0]">
                        <TableCell className="font-medium">{student.registrationNumber}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                                  {student.photoUrl ? (
                                      <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                      <span className="text-[10px] font-bold text-slate-500">
                                          {getFullName(student).split(' ').map(n => n[0]).join('').slice(0, 2)}
                                      </span>
                                  )}
                              </div>
                              {getFullName(student)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {student.status === 'present' && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Present
                            </Badge>
                          )}
                          {student.status === 'absent' && (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Absent
                            </Badge>
                          )}
                          {student.status === 'unmarked' && (
                            <Badge className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" variant="outline">Not Marked</Badge>
                          )}
                        </TableCell>
                        <TableCell>{student.reason || '-'}</TableCell>
                        <TableCell>
                          {student.markedAt ? format(new Date(student.markedAt), 'h:mm a') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface StudentAttendanceHistoryProps {
  classData: ClassData;
  selectedStudent: string | null;
  onStudentSelect: (studentId: string | null) => void;
  schoolID: string;
}

const StudentAttendanceHistory = ({ 
  classData, 
  selectedStudent, 
  onStudentSelect,
  schoolID 
}: StudentAttendanceHistoryProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const { toast } = useToast();

  const selectedStudentData = classData.students.find(s => s.id === selectedStudent);

  useEffect(() => {
    if (!selectedStudent || !schoolID) {
      setAttendanceRecords([]);
      return;
    }

    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - 30);
    const daysInPeriod = eachDayOfInterval({ start: startDate, end: selectedDate });
    const unsubscribePromises: (() => void)[] = [];

    daysInPeriod.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const recordsRef = collection(db, `schools/${schoolID}/attendance/${dateStr}/records`);
      const q = query(recordsRef, where('studentId', '==', selectedStudent));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const records = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: dateStr,
          reason: doc.data().reason ?? null
        } as AttendanceRecord));

        setAttendanceRecords(prev => {
          const filteredRecords = prev.filter(r => r.date !== dateStr || r.studentId !== selectedStudent);
          return [...filteredRecords, ...records];
        });

        if (process.env.NODE_ENV !== "production") {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Fetched records for student ${selectedStudent} on ${dateStr}:`, records);
        }
      }, (error) => {
        console.error(`Error fetching records for student ${selectedStudent} on ${dateStr}:`, error);
        toast({
          title: "Error",
          description: `Failed to load attendance records for ${dateStr}. Please check Firebase console for index errors.`,
          variant: "destructive",
          duration: 5000,
        });
        console.log(`To create the necessary Firestore index for ${dateStr}, use the following configuration in your Firebase console:`);
        console.log({
          collectionGroup: `schools/${schoolID}/attendance/${dateStr}/records`,
          queryScope: 'COLLECTION',
          fields: [
            { fieldPath: 'studentId', order: 'ASCENDING' }
          ]
        });
      });

      unsubscribePromises.push(unsubscribe);
    });

    return () => {
      unsubscribePromises.forEach(unsubscribe => unsubscribe());
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribed from student attendance listeners for ${selectedStudent}`);
      }
    };
  }, [selectedStudent, selectedDate, schoolID, toast]);

  const getRecordForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return attendanceRecords.find(r => r.date === dateStr && r.studentId === selectedStudent);
  };

  const getPeriodRecords = useMemo(() => {
    if (!selectedStudent) return [];

    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - 30);
    const allDays = eachDayOfInterval({ start: startDate, end: selectedDate }).filter(day => !isWeekend(day));

    return allDays.map((day, index) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const record = attendanceRecords.find(r => r.date === dateStr && r.studentId === selectedStudent);
      return {
        id: record ? record.id : `unmarked_${dateStr}_${index}`,
        studentId: selectedStudent,
        date: dateStr,
        status: record?.status === 'present' || record?.status === 'absent' ? record.status : 'unmarked',
        reason: record?.reason ?? null,
        markedBy: record?.markedBy ?? '',
        markedAt: record?.markedAt ?? '',
        classId: record?.classId ?? ''
      } as Omit<AttendanceRecord, 'status'> & { status: 'present' | 'absent' | 'unmarked' };
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [attendanceRecords, selectedDate, selectedStudent]);

  const calculateAttendanceStats = useMemo(() => {
    if (!selectedStudent || getPeriodRecords.length === 0) return null;

    const periodRecords = getPeriodRecords;
    const presentDays = periodRecords.filter(r => r.status === 'present').length;
    const absentDays = periodRecords.filter(r => r.status === 'absent').length;
    const unmarkedDays = periodRecords.filter(r => r.status === 'unmarked').length;
    const totalSchoolDays = periodRecords.length;
    const attendanceRate = totalSchoolDays > 0 ? Math.round((presentDays / totalSchoolDays) * 100) : 0;

    return {
      schoolDays: totalSchoolDays,
      presentDays,
      absentDays,
      totalMarkedDays: presentDays + absentDays,
      unmarkedDays,
      attendanceRate,
      records: periodRecords
    };
  }, [getPeriodRecords, selectedStudent]);

  const exportStudentHistory = () => {
    if (!selectedStudent || !selectedStudentData) return;
    
    const csvContent = [
      'Date,Status,Reason',
      ...getPeriodRecords.map(record => 
        `${record.date},${record.status},${record.reason || ''}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getFullName(selectedStudentData)}-attendance-last30-${format(selectedDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Student Attendance History
          </CardTitle>
          <CardDescription>View detailed attendance records for individual students</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="w-full">
              <Select 
                value={selectedStudent || ''} 
                onValueChange={(value) => onStudentSelect(value || null)}
              >
                <SelectTrigger className="w-full bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  <SelectValue placeholder="Select a student..." />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  {classData.students.map((student, index) => (
                    <SelectItem key={student.id || `student_${index}`} value={student.id} className="dark:hover:bg-[#1E293B] hover:bg-[#F1F5F9]">
                      <span className="hidden xs:inline">{getFullName(student)} ({student.registrationNumber})</span>
                      <span className="xs:hidden">{getFullName(student)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("justify-start text-left font-normal w-[180px]")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {selectedStudent && (
                <Button variant="outline" onClick={exportStudentHistory} className="text-sm px-3">
                  <Download className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Export</span>
                  <span className="xs:hidden">CSV</span>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedStudent && selectedStudentData && calculateAttendanceStats && (
        <>
          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{getFullName(selectedStudentData)}</CardTitle>
                  <CardDescription>
                    Reg No: {selectedStudentData.registrationNumber} • Last 30 days ending {format(selectedDate, 'MMMM d, yyyy')}
                  </CardDescription>
                </div>
                <div className={`text-3xl font-bold ${getAttendanceColor(calculateAttendanceStats.attendanceRate)}`}>
                  {calculateAttendanceStats.attendanceRate}%
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">{calculateAttendanceStats.presentDays}</div>
                    <div className="text-sm text-green-700">Present Days</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-xl font-bold text-red-600">{calculateAttendanceStats.absentDays}</div>
                    <div className="text-sm text-red-700">Absent Days</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{calculateAttendanceStats.totalMarkedDays}</div>
                    <div className="text-sm text-blue-700">Days Marked</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-xl font-bold text-gray-600">{calculateAttendanceStats.schoolDays}</div>
                    <div className="text-sm text-gray-700">School Days</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Attendance Rate</span>
                    <span className={getAttendanceColor(calculateAttendanceStats.attendanceRate)}>
                      {calculateAttendanceStats.attendanceRate}%
                    </span>
                  </div>
                  <Progress value={calculateAttendanceStats.attendanceRate} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Attendance for {format(selectedDate, 'MMM d, yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getRecordForDate(selectedDate) ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium text-sm">{format(new Date((getRecordForDate(selectedDate) as any).date), 'MMM d, yyyy')}</div>
                      <div className="text-xs text-muted-foreground">{format(new Date((getRecordForDate(selectedDate) as any).date), 'EEEE')}</div>
                    </div>
                    <div>
                      {(getRecordForDate(selectedDate) as any).status === 'present' ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Present
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Absent
                        </Badge>
                      )}
                    </div>
                  </div>
                  {(getRecordForDate(selectedDate) as any).reason && (
                    <div className="text-xs text-muted-foreground">
                      Reason: {(getRecordForDate(selectedDate) as any).reason}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Attendance not taken for this date.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Daily Records
              </CardTitle>
              <CardDescription>
                {getPeriodRecords.length} school days in last 30 days ending {format(selectedDate, 'MMMM d, yyyy')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {calculateAttendanceStats?.totalMarkedDays > 0 ? (
                <>
                  <div className="block sm:hidden">
                    <div className="space-y-3">
                      {getPeriodRecords.map(record => (
                        <div key={record.id} className="p-3 border border-[#1E293B] rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm">
                                {format(new Date(record.date), 'MMM d, yyyy')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(record.date), 'EEEE')}
                              </div>
                            </div>
                            <div>
                              {record.status === 'present' ? (
                                <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Present
                                </Badge>
                              ) : record.status === 'absent' ? (
                                <Badge variant="destructive" className="text-xs">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Absent
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Not Marked</Badge>
                              )}
                            </div>
                          </div>
                          {record.reason && (
                            <div className="text-xs text-muted-foreground">
                              Reason: {record.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="hidden sm:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Day</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getPeriodRecords.map(record => (
                          <TableRow key={record.id}>
                            <TableCell className="font-medium">
                              {format(new Date(record.date), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(record.date), 'EEEE')}
                            </TableCell>
                            <TableCell>
                              {record.status === 'present' ? (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Present
                                </Badge>
                              ) : record.status === 'absent' ? (
                                <Badge variant="destructive">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Absent
                                </Badge>
                              ) : (
                                <Badge variant="outline">Not Marked</Badge>
                              )}
                            </TableCell>
                            <TableCell>{record.reason || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No attendance records found for this period.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!selectedStudent && (
        <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] m-3">
          <CardContent className="text-center py-12">
            <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Select a student to view their attendance history</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AttendanceManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isSuspended, isLoading: suspensionLoading, schoolName: suspendedSchoolName } = useSuspensionCheck();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [classes, setClasses] = useState<{ id: string; className: string }[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [classData, setClassData] = useState<ClassData>({ id: '', className: '', students: [] });
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [present, setPresent] = useState(0);
  const [absent, setAbsent] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Authenticating user with uid:`, user.uid);
          }
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] ID Token Result claims:`, JSON.stringify(idTokenResult.claims, null, 2));
          }

          let selectedAffiliation: { role: string; schoolID?: string } | null = null;
          if (defaultAffiliation && defaultAffiliation.role === "school_staff" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "school_staff" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            if (process.env.NODE_ENV !== "production") {
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Invalid or missing school_staff claims, retrying after 10 seconds...`);
            }
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            let retrySelectedAffiliation: { role: string; schoolID?: string } | null = null;
            if (retryDefaultAff && retryDefaultAff.role === "school_staff" && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role === "school_staff" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              setHasValidClaims(false);
              setSchoolID(null);
              toast({
                title: "Missing Permissions",
                description: "You lack the necessary permissions to manage attendance. Please contact support.",
                variant: "destructive",
                duration: 5000,
              });
            }
          }
        } catch (error: any) {
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error verifying user claims:`, error);
          toast({
            title: "Authentication Error",
            description: "Failed to verify permissions. Please try again or contact support.",
            variant: "destructive",
            duration: 5000,
          });
          setHasValidClaims(false);
          setSchoolID(null);
        }
      } else {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No user logged in - deferring to App.tsx routing`);
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
      }
      setIsLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from onAuthStateChanged`);
      }
      unsubscribe();
    };
  }, [navigate, toast]);

  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping classes fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribeClasses = onSnapshot(classesRef, (snapshot) => {
      const classList = snapshot.docs.map(doc => ({
        id: doc.id,
        className: doc.data().name
      }));
      setClasses(classList.sort((a, b) => a.className.localeCompare(b.className)));
      if (classList.length > 0 && !selectedClass) {
        setSelectedClass(classList[0].id);
      }
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Classes fetched:`, classList);
      }
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching classes:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch classes. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setClasses([]);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from classes onSnapshot`);
      }
      unsubscribeClasses();
    };
  }, [schoolID, isLoading, hasValidClaims, selectedClass, toast]);

  useEffect(() => {
    if (!schoolID || !selectedClass || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping students fetch: schoolID=${schoolID}, selectedClass=${selectedClass}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const studentsQuery = query(studentsRef, where('currentClassUid', '==', selectedClass));
    const unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
      const students = snapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName,
        middleName: doc.data().middleName,
        lastName: doc.data().lastName,
        registrationNumber: doc.data().registrationNumber,
        photoUrl: doc.data().photoUrl
      }));
      setClassData({
        id: selectedClass,
        className: classes.find(c => c.id === selectedClass)?.className || '',
        students
      });
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Students fetched for class ${selectedClass}:`, students);
      }
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching students:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch students. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setClassData({ id: '', className: '', students: [] });
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from students onSnapshot`);
      }
      unsubscribeStudents();
    };
  }, [schoolID, selectedClass, isLoading, hasValidClaims, classes, toast]);

  useEffect(() => {
    if (!schoolID || !selectedClass) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const recordsRef = collection(db, `schools/${schoolID}/attendance/${dateStr}/records`);
    const q = query(recordsRef, where('classId', '==', selectedClass));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: dateStr,
        reason: doc.data().reason ?? null
      } as AttendanceRecord));
      setPresent(records.filter(doc => doc.status === 'present').length);
      setAbsent(records.filter(doc => doc.status === 'absent').length);
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Stats fetched for ${dateStr}:`, { present, absent, records });
      }
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching stats:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch attendance stats. Please check Firebase console for index errors.",
        variant: "destructive",
        duration: 5000,
      });
      console.log(`To create the necessary Firestore index for stats, use the following configuration in your Firebase console:`);
      console.log({
        collectionGroup: `schools/${schoolID}/attendance/${dateStr}/records`,
        queryScope: 'COLLECTION',
        fields: [
          { fieldPath: 'classId', order: 'ASCENDING' }
        ]
      });
    });

    return () => {
      unsubscribe();
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribed from stats snapshot`);
      }
    };
  }, [schoolID, selectedClass, selectedDate, toast]);

  const total = classData.students.length;
  const unmarked = total - (present + absent);
  const presentPercentage = total > 0 ? Math.round((present / total) * 100) : 0;
  const absentPercentage = total > 0 ? Math.round((absent / total) * 100) : 0;

  if (isSuspended) {
    return <SuspensionWarning schoolName={suspendedSchoolName} isLoading={suspensionLoading} />;
  }

  if (isLoading || suspensionLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <Teachersidebar />
          <div className="flex flex-col flex-1">
            <Teacherheader />
            <main className="flex-grow p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className="h-full border-r border-gray-200 dark:border-gray-700">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto bg-gray-50 dark:bg-gray-950">
            {isLoading ? (
              <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : hasValidClaims ? (
              <>
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Attendance Management</h1>
                  <p className="text-sm sm:text-base text-muted-foreground">Take attendance and track student records for your class</p>
                </div>

                <div className="mb-4">
                  <Select value={selectedClass || ''} onValueChange={setSelectedClass}>
                    <SelectTrigger className="w-[180px] bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <SelectValue placeholder="Select a class" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      {classes.map(cls => (
                        <SelectItem key={cls.id} value={cls.id} className="hover:bg-[#F1F5F9] dark:hover:bg-[#1E293B]">
                          {cls.className}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <Card className="p-3 sm:p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                    <CardHeader className="pb-2 sm:pb-3 p-0">
                      <div className="flex items-center justify-between">
                        <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
                        <span className="text-lg sm:text-2xl font-bold text-green-600">{present}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 pt-2">
                      <p className="text-xs sm:text-sm font-medium">Present Today</p>
                      <p className="text-xs text-muted-foreground">
                        {presentPercentage}% of class
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="p-3 sm:p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                    <CardHeader className="pb-2 sm:pb-3 p-0">
                      <div className="flex items-center justify-between">
                        <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
                        <span className="text-lg sm:text-2xl font-bold text-red-600">{absent}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 pt-2">
                      <p className="text-xs sm:text-sm font-medium">Absent Today</p>
                      <p className="text-xs text-muted-foreground">
                        {absentPercentage}% of class
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="p-3 sm:p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                    <CardHeader className="pb-2 sm:pb-3 p-0">
                      <div className="flex items-center justify-between">
                        <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" />
                        <span className="text-lg sm:text-2xl font-bold text-yellow-600">{unmarked}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 pt-2">
                      <p className="text-xs sm:text-sm font-medium">Not Marked</p>
                      <p className="text-xs text-muted-foreground">Need attention</p>
                    </CardContent>
                  </Card>
                  <Card className="p-3 sm:p-4 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                    <CardHeader className="pb-2 sm:pb-3 p-0">
                      <div className="flex items-center justify-between">
                        <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                        <span className="text-lg sm:text-2xl font-bold text-blue-600">{total}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 pt-2">
                      <p className="text-xs sm:text-sm font-medium">Total Students</p>
                      <p className="text-xs text-muted-foreground break-words">{classData.className}</p>
                    </CardContent>
                  </Card>
                </div>

                <Tabs defaultValue="take-attendance" className="space-y-4 sm:space-y-6">
                  <TabsList className="grid w-full grid-cols-3 h-auto bg-[#FFFFFF] dark:bg-[#1E293B] border-[#E2E8F0] dark:border-[#1E293B]">
                    <TabsTrigger 
                      value="take-attendance"
                      className={cn(
                        "data-[state=active]:bg-[#2F5FCD] data-[state=active]:text-white data-[state=active]:border-[#2F5FCD]",
                        "py-2 text-sm"
                      )}
                    >
                      Take Attendance
                    </TabsTrigger>
                    <TabsTrigger 
                      value="view-records"
                      className={cn(
                        "data-[state=active]:bg-[#2F5FCD] data-[state=active]:text-white data-[state=active]:border-[#2F5FCD]",
                        "py-2 text-sm"
                      )}
                    >
                      View Records
                    </TabsTrigger>
                    <TabsTrigger 
                      value="student-history"
                      className={cn(
                        "data-[state=active]:bg-[#2F5FCD] data-[state=active]:text-white data-[state=active]:border-[#2F5FCD]",
                        "py-2 text-sm"
                      )}
                    >
                      Student History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="take-attendance">
                    {selectedClass ? (
                      <AttendanceTaking 
                        classData={classData}
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                        schoolID={schoolID!}
                      />
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Select a class to take attendance.
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="view-records">
                    {selectedClass ? (
                      <AttendanceRecords 
                        classData={classData}
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                        schoolID={schoolID!}
                      />
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Select a class to view records.
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="student-history">
                    {selectedClass ? (
                      <StudentAttendanceHistory 
                        classData={classData}
                        selectedStudent={selectedStudent}
                        onStudentSelect={setSelectedStudent}
                        schoolID={schoolID!}
                      />
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Select a class to view student history.
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-lg text-red-600">No valid permissions to manage attendance.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AttendanceManagement;
